import { useLazyQuery, useMutation } from "@apollo/client";
import { Box, Button, Pagination, Paper, Stack } from "@mui/material";
import { useAppDispatch } from "hooks/redux";
import Loader from "pages/build-test/loader";
import { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import NoDataFound from "shared/molecules/no-data";
import { snackbarRequest } from "shared/molecules/snackbar/snackbar-slice";
import SectionTable from "shared/organisms/tables/table";
import entitesClient from "utils/graphql/entitiesClient";
import {
  DELETE_CHAPTER,
  DELETE_COURSE_CLASS_SUB_CHAP_TOPIC_MAP,
} from "utils/graphql/services/global-content-hierarchy/mutation";
import {
  GET_CHAPTER_LIST,
  GET_COURSE_CLASS_SUBJECT_CHAPTER_TOPIC_MAP,
} from "utils/graphql/services/global-content-hierarchy/queries";
import ContentRight from "shared/molecules/content-right";
import { TypographyHeading } from "shared/molecules/typography-layout";
import { makeStyles } from "@mui/styles";
import { indigo } from "@mui/material/colors";
import { table } from "./helper";
import { CsctMappingFilter } from "./filter";
import { isEmpty } from "utils/helpers";
import CollapsibleTable from "shared/organisms/tables/collapsible-table";

const useStyles: any = makeStyles((indigoTheme) => ({
  label: {
    textDecoration: "none",
    textAlign: "center",
    color: indigo[700],
    marginLeft: "80%",
  },
  Btn: {
    backgroundColor: "#3f50b5",
    color: "white",
    width: "90px",
  },
  BtnWrapper: {
    display: "flex",
    gap: "20px",
    justifyContent: "flex-end",
    paddingTop: "10px",
    paddingBottom: "10px",
  },
}));

export default function CourseClassSubChapTopMap() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [searchPayload, setSearchPayload] = useState({});

  const [clearValuesTrigger, setClearValuesTrigger] = useState(false);
  const [csctMappingTableData, setCsctMappingTableData] = useState([]);

  const [getCourseClassSubChapTopList, { loading, error, data }] = useLazyQuery(
    GET_COURSE_CLASS_SUBJECT_CHAPTER_TOPIC_MAP,
    {
      client: entitesClient,
      fetchPolicy: "no-cache",
    }
  );
  const [
    deleteCourseClassSubChaTopMap,
    { loading: loadingD, error: errorD, data: dataD },
  ] = useMutation(DELETE_COURSE_CLASS_SUB_CHAP_TOPIC_MAP, {
    client: entitesClient,
  });

  const convertHandleTabledata = (fetchData) => {
    const tableData = fetchData.map((item) => {
      let obj = { ...item };
      obj["course"] = item?.course?.course_name;
      obj["subject"] = item?.subject?.global_name;
      obj["chapter"] = item?.chapter?.global_name;
      obj["topic"] = item?.topic?.global_name;
      return obj;
    });
    setCsctMappingTableData(tableData);
  };

  const handleChange = (event, value) => {
    setPage(value); // pass new page number to parent
  };

  const handleDeleteCourseClassSubChaTopMap = async (id) => {
    try {
      const response: any = await deleteCourseClassSubChaTopMap({
        variables: {
          input: {
            id: id,
          },
        },
      });

      if (response?.data?.deleteCourseClassSubjectChapterTopicMapping?.success) {
        dispatch(
          snackbarRequest({
            message:
              `${response?.data?.deleteCourseClassSubjectChapterTopicMapping?.message}` ||
              "chapter deleted successfully",
          })
        );
        setClearValuesTrigger(!clearValuesTrigger);
      } else {
        dispatch(
          snackbarRequest({
            message: `${response?.data?.deleteCourseClassSubjectChapterTopicMapping?.message}`,
            type: "error",
          })
        );
      }
    } catch (err) {
      dispatch(snackbarRequest({ message: `${err}`, type: "error" }));
    }
  };

  useEffect(() => {
    getCourseClassSubChapTopList({
      variables: { filter: { ...searchPayload, page: page, limit } },
    });
  }, [searchPayload, page, limit]);

  useEffect(() => {
    const tableData =
      data?.getCourseClassSubjectChapterTopicMappings?.records || [];
    if (tableData.length > 0) {
      convertHandleTabledata(tableData);
    } else if (isEmpty(tableData)) {
      setCsctMappingTableData([]);
    }
  }, [data]);

  return (
    <>
      <ContentRight>
        <Link
          to="course_class_sub_chap_top_map/create"
          className={classes.label}
        >
          <Button variant="contained"> Create Mapping</Button>
        </Link>
      </ContentRight>
      <TypographyHeading sx={{ mb: 2, mt: 3, fontSize: "25px" }}>
        Course Class Subject Chapter Topic Mapping{" "}
      </TypographyHeading>
      <CsctMappingFilter
        setSearchPayload={setSearchPayload}
        setPage={setPage}
        clearValuesTrigger={clearValuesTrigger}
      />

      <Paper elevation={2} sx={{ px: 2, pb: 4, pt: 2, mt: 2 }}>
        <div>
          {loading && <Loader />}
          {!loading && csctMappingTableData?.length > 0 ? (
            <CollapsibleTable
              data={csctMappingTableData}
              table={table}
              id="id"
              handleEdit={""}
              handleCopy={""}
              rowsPerPageCount={data?.getCourseClassSubjectChapterTopicMappings?.meta?.total || 0}
              paginationSegmentApi="false"
              hasPageApiCall={false}
              showPaginationFooter={false}
            />
          ) : (
            <div>{!loading && <NoDataFound message="No Data" />}</div>
          )}
        </div>
        <Box sx={{ marginTop: "20px" }}>
          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            alignItems="center"
          >
            <Pagination
              count={
                Math.ceil(
                  data?.getCourseClassSubjectChapterTopicMappings?.meta?.total /
                    limit
                ) || 1
              }
              page={page}
              onChange={handleChange}
              color="primary"
              size="medium"
            />
          </Stack>
        </Box>
      </Paper>
    </>
  );
}
